import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography, Button, Table, Form, Modal, Select,
  InputNumber, DatePicker, Popconfirm, Space, message, Tag, Divider, Input, Switch,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, AuditOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../api';

const { Title, Text } = Typography;

const DIRECTION_OPTIONS = [
  { label: 'Inbound',  value: 'inbound'  },
  { label: 'Outbound', value: 'outbound' },
];

const CONTACT_TYPE_OPTIONS = [
  'Consult Followed by Transfer', 'Elevated', 'Original',
  'Takeover', 'Transfer to Agent', 'Transfer to Skill',
].map((v) => ({ label: v, value: v }));

const filterOption = (input, opt) =>
  (opt?.label ?? '').toLowerCase().includes(input.toLowerCase());

function fmtMonth(v) {
  return v ? dayjs(v, 'YYYY-MM').format('MMM YYYY') : null;
}

function OpenOrMonth({ v }) {
  return v ? <span>{fmtMonth(v)}</span> : <Text style={{ color: '#bfbfbf' }}>Open</Text>;
}

function SecRange({ min, max }) {
  if (min == null && max == null) return <span style={{ color: '#bfbfbf' }}>—</span>;
  const lo = min != null ? `${min}s` : '—';
  const hi = max != null ? `${max}s` : '—';
  return <span>{lo} – {hi}</span>;
}

// ── EvalCriteria section ─────────────────────────────────────────────────

function EvalCriteriaSection({ teams, jobs, campaigns }) {
  const [form]                    = Form.useForm();
  const [rows, setRows]           = useState([]);
  const [loading, setLoading]     = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving]       = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/eval-criteria');
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch { message.error('Failed to load eval criteria'); }
    finally  { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]); // eslint-disable-line

  const openAdd = () => { setEditingId(null); form.resetFields(); setModalOpen(true); };

  const openEdit = (row) => {
    setEditingId(row.ID);
    form.setFieldsValue({
      TeamID:      row.TeamID,
      JobID:       row.JobID,
      CampaignID:  row.CampaignID,
      MinCompleted: row.MinCompleted,
      FullTime:    row.FullTime,
      StartMonth:  dayjs(row.StartMonth, 'YYYY-MM'),
      EndMonth:    row.EndMonth ? dayjs(row.EndMonth, 'YYYY-MM') : null,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    const payload = {
      TeamID:      values.TeamID,
      JobID:       values.JobID,
      CampaignID:  values.CampaignID,
      MinCompleted: values.MinCompleted,
      FullTime:    values.FullTime,
      StartMonth:  values.StartMonth.format('YYYY-MM'),
      EndMonth:    values.EndMonth ? values.EndMonth.format('YYYY-MM') : null,
    };
    setSaving(true);
    try {
      if (editingId) await api.put(`/api/eval-criteria/${editingId}`, payload);
      else           await api.post('/api/eval-criteria', payload);
      setModalOpen(false);
      message.success(editingId ? 'Updated' : 'Created');
      load();
    } catch { message.error('Save failed'); }
    finally  { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/api/eval-criteria/${id}`); message.success('Deleted'); load(); }
    catch { message.error('Delete failed'); }
  };

  const columns = [
    { title: 'Team',     dataIndex: 'TeamName',     key: 'TeamName',     sorter: (a, b) => (a.TeamName || '').localeCompare(b.TeamName || '') },
    { title: 'Job',      dataIndex: 'JobName',      key: 'JobName',      sorter: (a, b) => (a.JobName || '').localeCompare(b.JobName || '') },
    { title: 'Campaign', dataIndex: 'CampaignName', key: 'CampaignName', sorter: (a, b) => (a.CampaignName || '').localeCompare(b.CampaignName || '') },
    { title: 'Full Time', dataIndex: 'FullTime', key: 'FullTime', width: 100, align: 'center', sorter: (a, b) => a.FullTime - b.FullTime, render: (v) => v === 1 ? 'Yes' : 'No' },
    { title: 'Min Completed / Month', dataIndex: 'MinCompleted', key: 'MinCompleted', width: 170, align: 'center', sorter: (a, b) => a.MinCompleted - b.MinCompleted },
    { title: 'Start Month', dataIndex: 'StartMonth', key: 'StartMonth', width: 130, sorter: (a, b) => a.StartMonth.localeCompare(b.StartMonth), render: (v) => fmtMonth(v) },
    { title: 'End Month',   dataIndex: 'EndMonth',   key: 'EndMonth',   width: 130, sorter: (a, b) => (a.EndMonth || '9999').localeCompare(b.EndMonth || '9999'), render: (v) => <OpenOrMonth v={v} /> },
    { title: '', key: 'actions', width: 80, render: (_, row) => (
      <Space>
        <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row)} />
        <Popconfirm title="Delete this criteria?" onConfirm={() => handleDelete(row.ID)} okText="Delete" okType="danger">
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      </Space>
    )},
  ];

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <Title level={4} style={{ margin: 0 }}>Eval Criteria</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>Add Criteria</Button>
      </div>
      <Table rowKey="ID" columns={columns} dataSource={rows} loading={loading} size="small" pagination={{ defaultPageSize: 50, hideOnSinglePage: true }} />

      <Modal maskClosable={false} title={editingId ? 'Edit Eval Criteria' : 'Add Eval Criteria'} open={modalOpen}
        onCancel={() => setModalOpen(false)} onOk={handleSave} okText="Save"
        confirmLoading={saving} destroyOnClose width={480}>
        <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item name="TeamID"      label="Team"     rules={[{ required: true, message: 'Required' }]}>
            <Select options={teams} showSearch filterOption={filterOption} placeholder="Select team" />
          </Form.Item>
          <Form.Item name="JobID"       label="Job"      rules={[{ required: true, message: 'Required' }]}>
            <Select options={jobs}  showSearch filterOption={filterOption} placeholder="Select job" />
          </Form.Item>
          <Form.Item name="CampaignID"  label="Campaign" rules={[{ required: true, message: 'Required' }]}>
            <Select options={campaigns} showSearch filterOption={filterOption} placeholder="Select campaign" />
          </Form.Item>
          <Form.Item name="FullTime" label="Full Time / Part Time" rules={[{ required: true, message: 'Required' }]}>
            <Select options={[{ label: 'Full Time', value: 1 }, { label: 'Part Time', value: 0 }]} placeholder="Select" />
          </Form.Item>
          <Form.Item name="MinCompleted" label="Min Completed Evals per Month" rules={[{ required: true, message: 'Required' }]}>
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="StartMonth" label="Start Month" rules={[{ required: true, message: 'Required' }]}>
            <DatePicker picker="month" format="MMMM YYYY" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="EndMonth" label="End Month" extra="Leave blank for open-ended">
            <DatePicker picker="month" format="MMMM YYYY" style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

// ── EvalCallConditions section ───────────────────────────────────────────

function CallConditionsSection({ campaigns }) {
  const [form]                        = Form.useForm();
  const [rows, setRows]               = useState([]);
  const [loading, setLoading]         = useState(false);
  const [modalOpen, setModalOpen]     = useState(false);
  const [editingId, setEditingId]     = useState(null);
  const [saving, setSaving]           = useState(false);
  const [skills, setSkills]           = useState([]);
  const [mediaOptions, setMediaOpts]  = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/eval-call-conditions');
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch { message.error('Failed to load call conditions'); }
    finally  { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]); // eslint-disable-line

  const loadOptions = useCallback(async (campaignId) => {
    if (!campaignId) { setSkills([]); setMediaOpts([]); return; }
    try {
      const r = await api.get('/api/eval-criteria/call-condition-options', { params: { campaign_id: campaignId } });
      setSkills((r.data.skills || []).map((s) => ({ label: `${s.Skill_Name} (#${s.Skill_No})`, value: s.Skill_No })));
      setMediaOpts((r.data.media_names || []).map((m) => ({ label: m, value: m })));
    } catch { setSkills([]); setMediaOpts([]); }
  }, []);

  const openAdd = () => {
    setEditingId(null);
    form.resetFields();
    setSkills([]);
    setMediaOpts([]);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row.ID);
    form.setFieldsValue({
      Title:             row.Title,
      IsActive:          row.IsActive !== 0,
      CampaignID:        row.CampaignID,
      Skill_No:          row.Skill_No || null,
      Direction:         row.Direction || null,
      MediaName:         row.MediaNameList || [],
      MinAgentTime:      row.MinAgentTime ?? null,
      MaxAgentTime:      row.MaxAgentTime ?? null,
      MinActiveTalkTime: row.MinActiveTalkTime ?? null,
      MaxActiveTalkTime: row.MaxActiveTalkTime ?? null,
      ContactType:       row.ContactTypeList || [],
    });
    loadOptions(row.CampaignID);
    setModalOpen(true);
  };

  const handleCampaignChange = (val) => {
    form.setFieldValue('Skill_No', null);
    form.setFieldValue('MediaName', []);
    loadOptions(val);
  };

  const handleSave = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    const payload = {
      Title:             values.Title,
      IsActive:          values.IsActive !== false,
      CampaignID:        values.CampaignID,
      Skill_No:          values.Skill_No || null,
      Direction:         values.Direction || null,
      MediaName:         values.MediaName || [],
      MinAgentTime:      values.MinAgentTime ?? null,
      MaxAgentTime:      values.MaxAgentTime ?? null,
      MinActiveTalkTime: values.MinActiveTalkTime ?? null,
      MaxActiveTalkTime: values.MaxActiveTalkTime ?? null,
      ContactType:       values.ContactType || [],
    };
    setSaving(true);
    try {
      if (editingId) await api.put(`/api/eval-call-conditions/${editingId}`, payload);
      else           await api.post('/api/eval-call-conditions', payload);
      setModalOpen(false);
      message.success(editingId ? 'Updated' : 'Created');
      load();
    } catch { message.error('Save failed'); }
    finally  { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/api/eval-call-conditions/${id}`); message.success('Deleted'); load(); }
    catch { message.error('Delete failed'); }
  };

  const columns = [
    { title: 'Active',   dataIndex: 'IsActive', key: 'IsActive', width: 70, align: 'center', render: (v) => <Switch size="small" checked={v !== 0} disabled /> },
    { title: 'Title',    dataIndex: 'Title',       key: 'Title',       width: 160, sorter: (a, b) => a.Title.localeCompare(b.Title) },
    { title: 'Campaign', dataIndex: 'CampaignName', key: 'CampaignName', sorter: (a, b) => (a.CampaignName || '').localeCompare(b.CampaignName || '') },
    { title: 'Skill',    key: 'Skill',   width: 140, render: (_, r) => r.SkillName ? <Text style={{ fontSize: 12 }}>{r.SkillName}</Text> : r.Skill_No ? <Text style={{ fontSize: 12 }}>#{r.Skill_No}</Text> : <Text style={{ color: '#bfbfbf' }}>—</Text> },
    { title: 'Direction', dataIndex: 'Direction', key: 'Direction', width: 100, render: (v) => v ? <Tag>{v}</Tag> : <Text style={{ color: '#bfbfbf' }}>—</Text> },
    { title: 'Media',    key: 'Media',   width: 140, render: (_, r) => r.MediaNameList?.length ? r.MediaNameList.map((m) => <Tag key={m} style={{ marginBottom: 2 }}>{m}</Tag>) : <Text style={{ color: '#bfbfbf' }}>—</Text> },
    { title: 'Agent Time (s)', key: 'AgentTime', width: 130, align: 'center', render: (_, r) => <SecRange min={r.MinAgentTime} max={r.MaxAgentTime} /> },
    { title: 'Active Talk (s)', key: 'TalkTime', width: 130, align: 'center', render: (_, r) => <SecRange min={r.MinActiveTalkTime} max={r.MaxActiveTalkTime} /> },
    { title: 'Contact Type', key: 'ContactType', width: 160, render: (_, r) => r.ContactTypeList?.length ? r.ContactTypeList.map((t) => <Tag key={t} style={{ marginBottom: 2 }}>{t}</Tag>) : <Text style={{ color: '#bfbfbf' }}>—</Text> },
    { title: '', key: 'actions', width: 80, render: (_, row) => (
      <Space>
        <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row)} />
        <Popconfirm title="Delete this condition?" onConfirm={() => handleDelete(row.ID)} okText="Delete" okType="danger">
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      </Space>
    )},
  ];

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <Title level={4} style={{ margin: 0 }}>Call Conditions</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>Add Condition</Button>
      </div>
      <Table rowKey="ID" columns={columns} dataSource={rows} loading={loading} size="small"
        pagination={{ defaultPageSize: 50, hideOnSinglePage: true }} scroll={{ x: 'max-content' }} />

      <Modal maskClosable={false} title={editingId ? 'Edit Call Condition' : 'Add Call Condition'} open={modalOpen}
        onCancel={() => setModalOpen(false)} onOk={handleSave} okText="Save"
        confirmLoading={saving} destroyOnClose width={560}>
        <Form form={form} layout="vertical" style={{ marginTop: 12 }} initialValues={{ IsActive: true }}>
          <div style={{ display: 'flex', gap: 12 }}>
            <Form.Item name="Title" label="Title" rules={[{ required: true, message: 'Required' }, { max: 50 }]} style={{ flex: 1 }}>
              <Input maxLength={50} showCount />
            </Form.Item>
            <Form.Item name="IsActive" label="Active" valuePropName="checked">
              <Switch />
            </Form.Item>
          </div>
          <Form.Item name="CampaignID" label="Campaign" rules={[{ required: true, message: 'Required' }]}>
            <Select options={campaigns} showSearch filterOption={filterOption} placeholder="Select campaign" onChange={handleCampaignChange} />
          </Form.Item>
          <Form.Item name="Skill_No" label="Skill (optional)">
            <Select options={skills} allowClear showSearch filterOption={filterOption}
              placeholder={skills.length ? 'Select skill' : 'Select a campaign first'} />
          </Form.Item>
          <Form.Item name="Direction" label="Direction (optional)">
            <Select options={DIRECTION_OPTIONS} allowClear placeholder="Any direction" />
          </Form.Item>
          <Form.Item name="MediaName" label="Media Name (optional)">
            <Select mode="tags" options={mediaOptions} placeholder="Type or select media names"
              tokenSeparators={[',']} />
          </Form.Item>
          <Form.Item name="ContactType" label="Contact Type (optional)">
            <Select mode="multiple" options={CONTACT_TYPE_OPTIONS} allowClear
              placeholder="Any contact type" />
          </Form.Item>
          <div style={{ display: 'flex', gap: 12 }}>
            <Form.Item name="MinAgentTime" label="Min Agent Time (sec)" style={{ flex: 1 }}>
              <InputNumber min={0} style={{ width: '100%' }} placeholder="No min" />
            </Form.Item>
            <Form.Item name="MaxAgentTime" label="Max Agent Time (sec)" style={{ flex: 1 }}>
              <InputNumber min={0} style={{ width: '100%' }} placeholder="No max" />
            </Form.Item>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <Form.Item name="MinActiveTalkTime" label="Min Active Talk Time (sec)" style={{ flex: 1 }}>
              <InputNumber min={0} style={{ width: '100%' }} placeholder="No min" />
            </Form.Item>
            <Form.Item name="MaxActiveTalkTime" label="Max Active Talk Time (sec)" style={{ flex: 1 }}>
              <InputNumber min={0} style={{ width: '100%' }} placeholder="No max" />
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────

export default function EvalCriteriaPage() {
  const [teams, setTeams]         = useState([]);
  const [jobs, setJobs]           = useState([]);
  const [campaigns, setCampaigns] = useState([]);

  useEffect(() => {
    api.get('/api/teams').then((r) => setTeams(r.data.map((t) => ({ label: t.Name, value: t.ID })))).catch(() => {});
    api.get('/api/jobs').then((r) => setJobs(r.data.map((j) => ({ label: j.JobName, value: j.JobID })))).catch(() => {});
    api.get('/api/agent-stats/options').then((r) => setCampaigns((r.data.campaigns || []).map((c) => ({ label: c.Name, value: c.ID })))).catch(() => {});
  }, []);

  return (
    <div style={{ padding: '16px 20px' }}>
      <Title level={3} style={{ marginBottom: 20 }}>
        <AuditOutlined style={{ marginRight: 8 }} />Eval Criteria
      </Title>

      <EvalCriteriaSection teams={teams} jobs={jobs} campaigns={campaigns} />

      <Divider />

      <CallConditionsSection campaigns={campaigns} />
    </div>
  );
}
